import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// fields: [{ key, label, type: 'text'|'textarea'|'number'|'select'|'checkbox'|'image'|'list', options?, full? }]
export default function CrudManager({ title, subtitle, endpoint, queryKey, fields, columns, testid }) {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // 'add' | item
  const { data: items = [], isLoading } = useQuery({ queryKey: [queryKey], queryFn: () => api.get(endpoint.public).then((r) => r.data) });
  const invalidate = () => qc.invalidateQueries({ queryKey: [queryKey] });

  const del = useMutation({
    mutationFn: (id) => api.delete(`${endpoint.admin}/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
    onError: () => toast.error("Delete failed"),
  });

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-heading font-bold text-navy-heading">{title}</h1><p className="text-slate-500 text-sm">{subtitle}</p></div>
        <button onClick={() => setModal("add")} className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-4 py-2.5 rounded-xl text-sm" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /> Add</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>{columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium">{c.label}</th>)}<th className="px-4 py-3 font-medium">Actions</th></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={columns.length + 1} className="p-10 text-center text-slate-400">Loading…</td></tr> :
              items.length === 0 ? <tr><td colSpan={columns.length + 1} className="p-10 text-center text-slate-400">Nothing yet. Click Add to create one.</td></tr> :
              items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-slate-600">
                      {c.render ? c.render(it) : (typeof it[c.key] === "boolean" ? (it[c.key] ? "Yes" : "No") : String(it[c.key] ?? "—")).slice(0, 60)}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => setModal(it)} className="text-brand hover:bg-brand-light p-2 rounded-lg" data-testid={`${testid}-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => { if (window.confirm("Delete this item?")) del.mutate(it.id); }} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`${testid}-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal && <FormModal item={modal === "add" ? null : modal} fields={fields} endpoint={endpoint} title={title} onClose={() => setModal(null)} onSaved={() => { setModal(null); invalidate(); }} />}
    </div>
  );
}

function FormModal({ item, fields, endpoint, title, onClose, onSaved }) {
  const init = {};
  fields.forEach((f) => { init[f.key] = item ? (item[f.key] ?? (f.type === "list" ? [] : f.type === "checkbox" ? false : "")) : (f.type === "checkbox" ? true : f.type === "list" ? [] : ""); });
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      if (item) await api.put(`${endpoint.admin}/${item.id}`, payload);
      else await api.post(endpoint.admin, payload);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setLoading(false); }
  };

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h2 className="font-heading font-bold text-navy-heading text-lg">{item ? "Edit" : "Add"} {title.replace(/s$/, "")}</h2><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key} className={f.full || f.type === "textarea" || f.type === "list" ? "sm:col-span-2" : ""}>
              <label className="block text-xs font-semibold text-navy-heading mb-1.5">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea rows={f.rows || 3} className={inp} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} />
              ) : f.type === "select" ? (
                <select className={inp} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-slate-600 mt-2"><input type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} className="w-4 h-4" /> {f.checkboxLabel || "Enabled"}</label>
              ) : f.type === "list" ? (
                <textarea rows={f.rows || 3} className={inp} placeholder="One item per line" value={Array.isArray(form[f.key]) ? form[f.key].join("\n") : form[f.key]} onChange={(e) => set(f.key, e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
              ) : f.type === "number" ? (
                <input type="number" className={inp} value={form[f.key]} onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))} />
              ) : (
                <input className={inp} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} />
              )}
              {f.type === "image" && form[f.key] && <img src={form[f.key]} alt="preview" className="mt-2 h-16 rounded-lg object-cover" />}
            </div>
          ))}
        </div>
        <button onClick={save} disabled={loading} className="mt-5 w-full flex items-center justify-center gap-2 bg-brand text-white font-semibold py-3 rounded-xl disabled:opacity-70" data-testid="crud-save-btn">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
      </div>
    </div>
  );
}
